import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageArea from '../shared/PageArea';
import PageTitle from '../shared/PageTitle';
import LoadingWheel from '../shared/LoadingWheel';
import { StatusChip, projectStatusTone } from '../shared/StatusChip';
import { ProjectsClient, ProjectListItem } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';
import { usePermissions, Permissions } from '../../contexts/PermissionContext';

const pageSize = 25;

const ProjectsPage: React.FC<{ conflictsOnly?: boolean }> = ({ conflictsOnly = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const client = useMemo(() => new ProjectsClient(getApiBaseUrl(), authenticatedFetch), []);

  const [items, setItems] = useState<ProjectListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ externalClientName: '', externalCompanyName: '', cargoDescription: '', externalReference: '' });

  const load = () => {
    setLoading(true);
    client
      .getApiProjects(page, pageSize, undefined, conflictsOnly)
      .then((r) => {
        setItems(r.items ?? []);
        setTotal(r.totalCount ?? 0);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, page, conflictsOnly]);

  const submit = async () => {
    const createdId = await client.postApiProjects(form);
    setOpen(false);
    setForm({ externalClientName: '', externalCompanyName: '', cargoDescription: '', externalReference: '' });
    navigate(`/projects/${createdId}`);
  };

  return (
    <PageArea>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
        <PageTitle title={t(conflictsOnly ? 'projects.conflicts' : 'projectsPage.title')}
          subtitle={t(conflictsOnly ? 'projects.conflictsInboxHint' : 'projectsPage.subtitle')} />
        {!conflictsOnly && hasPermission(Permissions.PROJECT_MANAGE) && (
          <Button variant="primary" size="md" onClick={() => setOpen(true)}>
            {t('projectsPage.newProject')}
          </Button>
        )}
      </Stack>
      {loading ? (
        <LoadingWheel />
      ) : items.length === 0 ? (
        <Typography color="text.secondary">{t(conflictsOnly ? 'projects.noOpenConflicts' : 'common.noData')}</Typography>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('projectsPage.reference')}</TableCell>
                <TableCell>{t('projectsPage.projectNumber')}</TableCell>
                <TableCell>{t('projectsPage.client')}</TableCell>
                <TableCell>{t('projectsPage.cargo')}</TableCell>
                <TableCell>{t('projectsPage.vessel')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell align="right">{t('projectsPage.documents')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((p) => (
                <TableRow
                  key={p.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/projects/${p.id}${conflictsOnly ? '?tab=conflicts' : ''}`)}
                >
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{p.internalReference}</TableCell>
                  <TableCell>{p.projectNumber}</TableCell>
                  <TableCell>{p.externalClientName}</TableCell>
                  <TableCell>{p.cargoDescription}</TableCell>
                  <TableCell>{p.vesselName}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <StatusChip label={p.status ?? ''} tone={projectStatusTone(p.status)} />
                      {p.conflictStatus && <StatusChip label={t(`projects.status.${p.conflictStatus}`)} tone={p.conflictStatus === 'PotentialConflict' ? 'warning' : 'default'} />}
                      {p.needsReviewCount ? (
                        <StatusChip
                          label={t('projectsPage.needsReviewCount', {
                            count: p.needsReviewCount,
                          })}
                          tone="warning"
                        />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{p.documentCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(total / pageSize))}
              page={page}
              onChange={(_, value) => setPage(value)}
            />
          </Box>
        </>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('projectsPage.newProject')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('projectsPage.client')}
              value={form.externalClientName}
              onChange={(e) => setForm({ ...form, externalClientName: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('projects.company')}
              value={form.externalCompanyName}
              onChange={(e) => setForm({ ...form, externalCompanyName: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('projectsPage.cargo')}
              value={form.cargoDescription}
              onChange={(e) => setForm({ ...form, cargoDescription: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('upload.clientReference')}
              value={form.externalReference}
              onChange={(e) => setForm({ ...form, externalReference: e.target.value })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="md" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" size="md" onClick={submit}>
            {t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default ProjectsPage;
